// src/firebase/firebase.js
import { initializeApp } from 'firebase/app';
import {
  getAuth,
  RecaptchaVerifier,
  connectAuthEmulator,
} from 'firebase/auth';
import {
  initializeFirestore,
  connectFirestoreEmulator,
  serverTimestamp,
  setLogLevel,
} from 'firebase/firestore';
import {
  getMessaging,
  isSupported as isMessagingSupported,
} from 'firebase/messaging';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check';

/* ------------------------------ Config/env ------------------------------- */
export const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const DISABLE_PHONE_RECAPTCHA = import.meta.env.VITE_DISABLE_RECAPTCHA_PHONE_AUTH === 'true';
const ENABLE_APPCHECK         = import.meta.env.VITE_ENABLE_APPCHECK === 'true';
const USE_EMULATORS           = import.meta.env.VITE_USE_FIREBASE_EMULATORS === 'true';

// Defaults to us-central1
const FUNCTIONS_REGION        = import.meta.env.VITE_FIREBASE_FUNCTIONS_REGION || 'us-central1';

// Firestore transport tuning
const FB_FORCE_LONG_POLLING   = import.meta.env.VITE_FB_FORCE_LONG_POLLING === 'true';
const FB_AUTO_DETECT_LP       = (import.meta.env.VITE_FB_AUTO_DETECT_LONG_POLLING ?? 'true') !== 'false';
const FB_USE_FETCH_STREAMS    = (import.meta.env.VITE_FB_USE_FETCH_STREAMS ?? 'false') === 'true';
const FB_LP_TIMEOUT_SECS      = Number(import.meta.env.VITE_FB_LONG_POLL_TIMEOUT ?? 30);

// App Check site key
const APPCHECK_SITE_KEY =
  import.meta.env.VITE_RECAPTCHA_ENTERPRISE_SITE_KEY ||
  import.meta.env.VITE_RECAPTCHA_SITE_KEY ||
  '';

const IS_LOCAL =
  typeof window !== 'undefined' &&
  ['localhost', '127.0.0.1'].includes(window.location.hostname);

/* ------------------------------- Init app -------------------------------- */
export const app = initializeApp(firebaseConfig);
export const PROJECT_ID = app.options.projectId;

/**
 * App Check initialization
 */
(function initAppCheckSafely() {
  if (!ENABLE_APPCHECK || !APPCHECK_SITE_KEY) return;

  try {
    if (IS_LOCAL) {
      // eslint-disable-next-line no-undef
      self.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
    }
    initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(APPCHECK_SITE_KEY),
      isTokenAutoRefreshEnabled: true,
    });
  } catch (err) {
    // Silent fail in production
  }
})();

/* ----------------------- Create service instances ------------------------ */
export const auth = getAuth(app);

// Firestore with robust transport fallbacks
const fsOpts = {
  ignoreUndefinedProperties: true,
  useFetchStreams: FB_USE_FETCH_STREAMS,
  experimentalLongPollingOptions: { timeoutSeconds: FB_LP_TIMEOUT_SECS },
};

if (FB_FORCE_LONG_POLLING) {
  fsOpts.experimentalForceLongPolling = true;
} else if (FB_AUTO_DETECT_LP) {
  fsOpts.experimentalAutoDetectLongPolling = true;
}

export const db = initializeFirestore(app, fsOpts);

// Quieter Firestore logs in prod
setLogLevel(import.meta.env.PROD ? 'error' : 'warn');

export const storage   = getStorage(app);
export const functions = getFunctions(app, FUNCTIONS_REGION);

// Messaging (safely initialized)
export let messaging = null;
(async () => {
  try {
    if (await isMessagingSupported()) {
      messaging = getMessaging(app);
    }
  } catch {
    // Messaging not supported or failed
  }
})();

/* --------------------------- Local emulators ----------------------------- */
if (USE_EMULATORS && IS_LOCAL) {
  try { connectAuthEmulator(auth, 'http://127.0.0.1:9099'); } catch {}
  try { connectFirestoreEmulator(db, '127.0.0.1', 8080); } catch {}
  try { connectFunctionsEmulator(functions, '127.0.0.1', 5001); } catch {}
  try { connectStorageEmulator(storage, '127.0.0.1', 9199); } catch {}
}

/* ------------------------- Phone auth test guard ------------------------- */
if (DISABLE_PHONE_RECAPTCHA) {
  try {
    if (auth && auth.settings) {
      // @ts-ignore
      auth.settings.appVerificationDisabledForTesting = true;
    }
  } catch (e) {
    // Silent fail
  }
}

/* --------------------------- reCAPTCHA helper ---------------------------- */
export function getRecaptchaVerifier(containerId = 'recaptcha-container') {
  if (typeof window !== 'undefined' && window.recaptchaVerifier) {
    try { window.recaptchaVerifier.clear(); } catch {}
    // @ts-ignore
    window.recaptchaVerifier = null;
  }
  const verifier = new RecaptchaVerifier(auth, containerId, { size: 'invisible' });
  // @ts-ignore
  window.recaptchaVerifier = verifier;
  return verifier;
}

/* ------------------------------- Exports -------------------------------- */
export { serverTimestamp };
export const isRecaptchaEnabled = !DISABLE_PHONE_RECAPTCHA;
export const isAppCheckEnabled  = ENABLE_APPCHECK;